import React, { useEffect } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import GradientDivider from 'components/lib/GradientDivider';
import TextArea from 'components/lib/TextArea';
import DefaultItemRender from 'components/lib/UploadDefaultItemRender';

import CloseIcon from '../../../common/Icons/CloseIcon';
import ShopDataModalAddButtonPill from '../common/ShopDataModalAddButtonPill';
import ShopDataModalContentHeader from '../common/ShopDataModalContentHeader';
import { FIELD_CATEGORY } from '../constants';
import ShopDataModalDraggerField from '../documents/ShopDataModalDraggerField';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCloseIcon = styled.div`
  display: flex;
  position: absolute;
  right: -35px;
  top: 2px;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;

  align-items: center;
  .ant-upload-wrapper .ant-upload-drag {
    width: auto;
  }
  .ant-upload-wrapper .ant-upload-list .ant-upload-list-item-container {
    width: 100%;
    margin-left: 15px;
  }
`;

const CenterFieldWrapper = styled.div`
  display: flex;
  width: 80%;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px 0px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
`;

const ShopDataModalShopNotes = () => {
  const { form, currentData, disabled, setCurrentData, handleDataStatusChanged } = useShopDataModalContext();

  const handleValuesChange = (values: any, allValues: any) => {
    handleDataStatusChanged([FIELD_CATEGORY.SHOP_NOTES]);
    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.SHOP_NOTES]: allValues[FIELD_CATEGORY.SHOP_NOTES],
    }));
  };

  useEffect(() => {
    form?.setFieldsValue({
      [FIELD_CATEGORY.SHOP_NOTES]: currentData[FIELD_CATEGORY.SHOP_NOTES],
    });
  }, [currentData[FIELD_CATEGORY.SHOP_NOTES], form]);

  return (
    <Container key={currentData}>
      <Form form={form} disabled={disabled} onValuesChange={handleValuesChange}>
        <ShopDataModalContentHeader title="Shop Notes" />
        <Form.List name={[FIELD_CATEGORY.SHOP_NOTES]}>
          {(fields, { add, remove }) => {
            const shopNotes = form?.getFieldValue([FIELD_CATEGORY.SHOP_NOTES]) || [];

            return (
              <ItemsContainer>
                {fields.map(({ key, name, ...restField }) => {
                  return (
                    <Item key={key}>
                      <Content>
                        <StyledCloseIcon
                          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                          onClick={() => {
                            if (!disabled) {
                              remove(name);
                              if (shopNotes.length === 1) {
                                add();
                              }
                            }
                          }}
                          data-test-id="sd-modal-shop-notes-clear-btn"
                        >
                          <CloseIcon size={16} />
                        </StyledCloseIcon>
                        <Form.Item noStyle name={[name, 'id']} />
                        <Form.Item noStyle name={[name, 'request_notes']}>
                          <TextArea
                            style={{ width: '100%', resize: 'vertical' }}
                            rows={5}
                            placeholder="Enter some notes to display for the client and the reviewer."
                            data-test-id="sd-modal-shop-note-input"
                          />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const currentAttachments = getFieldValue([FIELD_CATEGORY.SHOP_NOTES, name, 'attachment']);
                            return (
                              <ShopDataModalDraggerField
                                name={[name, 'attachment']}
                                label="files"
                                maxCount={1}
                                fileList={currentAttachments ?? []}
                                itemRender={(originNode: React.ReactNode, file: any, fileList: any, actions: any) =>
                                  DefaultItemRender(originNode, file, fileList, actions, disabled)
                                }
                                dataTestId="sd-modal-shop-notes-files-input"
                              />
                            );
                          }}
                        </Form.Item>
                      </Content>
                      {<GradientDivider />}
                    </Item>
                  );
                })}
                <CenterFieldWrapper>
                  <ShopDataModalAddButtonPill label={'Add Another Note'} onClick={() => add()} dataTestId="sd-modal-shop-notes-add-note-btn"/>
                </CenterFieldWrapper>
              </ItemsContainer>
            );
          }}
        </Form.List>
      </Form>
    </Container>
  );
};

export default ShopDataModalShopNotes;
